import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AvailableCharitiesModal, DonateToCharityModal } from '@hero/components/modals';
import { useValidationErrorsStore } from '@hero/components/providers/ValidationErrorsProvider';
import { FEATURE_TYPES } from '@hero/constants';
import { useGetCharity } from '@hero/hooks';
import { PageLayout } from '@hero/layouts';
import { ChooseFeatureTypeModal, ProductSelectionSection } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useShoppingCart } from '@hero/store/hooks';

export default function GiftCatalog({ pageData }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { products, updateProducts } = useShoppingCart();
  const { charityEnabled, isCharityExist, anyCharityItemExists } = useGetCharity();

  const hasCharity = anyCharityItemExists(products);

  const [showCharityInfoModal, setShowCharityInfoModal] = useState(false);
  const [showSelectCharitiesModal, setShowSelectCharitiesModal] = useState(false);
  const [chooseFeatureModalIsOpen, setChooseFeatureModalIsOpen] = useState(false);
  const { addError } = useValidationErrorsStore();

  const onBack = () => navigate(ROUTE_PATHS.HOME);
  const onNext = () => {
    if (products.length === 0) {
      addError(
        <Trans
          key="noProductAtList"
          i18nKey="errors.noSelectedProduct"
          components={{
            b: <b />
          }}
        />
      );

      return;
    }

    if (charityEnabled && !hasCharity && isCharityExist) {
      return setShowCharityInfoModal(true);
    }
    return setChooseFeatureModalIsOpen(true);
  };

  return (
    <PageLayout
      pageData={pageData}
      header={{
        title: t('giftCatalog'),
        step: 1,
        backButton: {
          onClick: onBack
        }
      }}
      navigation={{
        leftButton: {
          children: t('back'),
          onClick: onBack
        },
        rightButton: {
          icon: true,
          children: t('continueWithSelectedGifts'),
          onClick: onNext
        }
      }}
    >
      <ProductSelectionSection
        selectedProducts={products}
        onChangeSelectedProductList={selectedProducts => {
          updateProducts(selectedProducts);
        }}
        isDraft
        featureType={FEATURE_TYPES.recognition}
      />
      <ChooseFeatureTypeModal
        isOpen={chooseFeatureModalIsOpen}
        onClose={() => {
          setChooseFeatureModalIsOpen(false);
        }}
      />
      {showCharityInfoModal && (
        <DonateToCharityModal
          isOpen={showCharityInfoModal}
          setIsOpen={setShowCharityInfoModal}
          primaryButtonProps={{
            onClick: () => {
              setShowCharityInfoModal(false);
              setShowSelectCharitiesModal(true);
            },
            children: t('yes')
          }}
          secondaryButtonProps={{
            onClick: () => {
              setShowCharityInfoModal(false);
              setChooseFeatureModalIsOpen(true);
            },
            children: t('skip')
          }}
          descriptionTextKey="donateToCharityDesc2"
          titleKey="donateToCharityTitle2"
        />
      )}
      {showSelectCharitiesModal && (
        <AvailableCharitiesModal
          isOpen={showSelectCharitiesModal}
          setIsOpen={setShowSelectCharitiesModal}
          setSelectedCharities={selectedCharities =>
            updateProducts([...products, ...selectedCharities])
          }
        />
      )}
    </PageLayout>
  );
}
